import { toDate, formatInTimeZone } from "date-fns-tz";
import { ROLES } from "../../model/roles";
// Importa React se usi JSX in metodi come generateIconContenuti e il file è .js
// Ad esempio: import React from 'react';

export default class UtilityService {

  static isInGroup = (role = "", authRole = []) => {
    return authRole?.includes(role);
  };

  static isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  static getModalitaErogazione = (inputValue) => {
    switch (inputValue) {
        case 'Telematica_Asincrona':
            return 'Telematica asincrona';
        case 'Telematica_Sincrona':
        case 'Telematica_sincrona': // Considera se questo duplicato è intenzionale
            return 'Telematica sincrona';
        case 'Presenza':
            return 'Presenza';
        case 'Telematica_Mista':
            return 'Telematica mista';
        case 'Mista_Presenza/Telematica':
            return 'Presenza/Telematica';
        default:
            return 'Nessuna opzione';
    }
  }; // La classe NON si chiude qui

  static showAlert(message, type, duration = 5000) {
    const alertDiv = document.createElement('div');
    alertDiv.id = "notification";
    alertDiv.classList.add('alert', `alert-${type}`);
    // Per innerHTML, usa 'class' standard, non 'className' di React
    alertDiv.innerHTML = `<i class="${UtilityService.getIconClass(type)}"></i><span class="ms-2">${message}</span>`;
    document.body.appendChild(alertDiv);
    setTimeout(() => {
      const notificationElement = document.getElementById("notification");
      if (notificationElement) {
        notificationElement.remove();
      }
    }, duration);
  }

  static getIconClass(type) {
    switch (type) {
      case 'success':
        return 'bi bi-check-circle';
      case 'danger':
        return 'bi bi-exclamation-triangle';
      case 'warning':
        return 'bi bi-exclamation-triangle'; // Uguale a danger, valuta se differenziare
      default:
        return 'bi bi-info-circle';
    }
  }

  /**
   * true se la stringa sembra "YYYY-MM-DD HH:mm" dal backend **senza** offset/Z (es. MySQL), da intendere a Roma.
   * In quel caso new Date() non è adatto (dipende dal browser / interpretazione come UTC o locale errata).
   */
  static _isBackendNaiveDateTimeString(s) {
    if (typeof s !== "string" || !s) {
      return false;
    }
    const t = s.trim();
    if (!/^\d{4}-\d{2}-\d{2}[ T]\d{1,2}:\d{2}/.test(t)) {
      return false;
    }
    if (/[zZ]/.test(t)) {
      return false;
    }
    if (/[+-]\d{2}:?\d{2}$/.test(t)) {
      return false;
    }
    return true;
  }

  /**
   * Se "true" (env REACT_APP_MYSQL_NAIVE_IS_UTC), le stringhe YYYY-MM-DD senza fuso sono trattate come **UTC** (es. MySQL in UTC) e mostrate a Roma. Default: falso = orario "naive" = già in Europa/Roma (es. `NOW()` con `date_default_timezone=Europe/Rome` sul server in Italia).
   */
  static _mysqlNaiveIsUtc() {
    const v = process.env.REACT_APP_MYSQL_NAIVE_IS_UTC;
    return v === "true" || v === "1";
  }

  /**
   * Converte input API in Date.
   * Stringhe MySQL/PHP **senza Z/offset**:
   * - per default: interpretate come orario a parete in **Europe/Rome** (stesso criterio di `toDate` date-fns-tz);
   * - con REACT_APP_MYSQL_NAIVE_IS_UTC=true: interpretate come orario **UTC** e riconvertite in fuso in output.
   */
  static _parseDateTimeForDisplay(data) {
    if (data == null || data === "") {
      return null;
    }
    if (data instanceof Date) {
      return isNaN(data.getTime()) ? null : data;
    }
    if (typeof data === "number") {
      const d = new Date(data);
      return isNaN(d.getTime()) ? null : d;
    }
    const s = String(data).trim();
    if (!s) {
      return null;
    }
    if (UtilityService._isBackendNaiveDateTimeString(s)) {
      const normalized = s.replace(" ", "T").slice(0, 19);
      if (UtilityService._mysqlNaiveIsUtc()) {
        const d = new Date(`${normalized}Z`);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = toDate(normalized, { timeZone: "Europe/Rome" });
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * `email_consegna_aggiornato_at` dal job PHP: spesso MySQL `DATETIME` senza fuso = valore in **UTC** (o in istante unico)
   * da mostrare in **ora locale italiana** (es. 13:10 UTC → 15:10 in CEST) senza etichette tipo "GMT+2".
   * Se invece il DB salva già l'ora di parete italiana, impostare `REACT_APP_BOUNCE_STAMP_AS_LOCAL_ROMA=true`.
   */
  static _bounceStampDbIsLocalRome() {
    const v = process.env.REACT_APP_BOUNCE_STAMP_AS_LOCAL_ROMA;
    return v === "true" || v === "1";
  }

  static formatBounceRilevamentoAt(data) {
    if (data == null || data === "") {
      return null;
    }
    const s = String(data).trim();
    if (!s) {
      return null;
    }
    let d;
    if (UtilityService._isBackendNaiveDateTimeString(s)) {
      const normalized = s.replace(" ", "T").slice(0, 19);
      if (UtilityService._bounceStampDbIsLocalRome()) {
        d = toDate(normalized, { timeZone: "Europe/Rome" });
      } else {
        d = new Date(`${normalized}Z`);
      }
    } else {
      d = new Date(s);
    }
    if (isNaN(d.getTime())) {
      return null;
    }
    return formatInTimeZone(d, "Europe/Rome", "dd/MM/yyyy, HH:mm");
  }

  /**
   * Data/ora in Europe/Rome (stessa logica di _parseDateTimeForDisplay, senza suffissi offset).
   */
  static formatDateAndHour = (data) => {
    const d = UtilityService._parseDateTimeForDisplay(data);
    if (!d) {
      return null;
    }
    return formatInTimeZone(d, "Europe/Rome", "dd/MM/yyyy, HH:mm");
  };

  static formatDate = (input) => {
    if (!input) return null; // Gestisci input nullo
    const date = new Date(input);
    if (isNaN(date.getTime())) return null; // Gestisci data non valida

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const formatDay = day <= 9 ? '0' + day : day;
    const formatMonth = month <= 9 ? '0' + month : month;
    const formatDate = `${formatDay}/${formatMonth}/${date.getFullYear()}`;
    return formatDate;
  };

  static formatModalitaErogazione = (inputValue) => {
    switch (inputValue) {
        case 'asincrona':
            return 'Telematica_Asincrona';
        case 'sincrona':
            return 'Telematica_Sincrona';
        case 'presenza':
            return 'Presenza';
        case 'telematica_mista':
            return 'Telematica_Mista';
        case 'mista_presenza/telematica':
          return 'Mista_Presenza/Telematica';       
        default:
            return 'Nessun opzione'; // 'Nessuna opzione' con la 'a' se preferito
    }
  };

  static formatModalitaErogazionePerCheckbox = (inputValue) => {
    switch (inputValue) {
        case 'Telematica_Asincrona':
            return 'asincrona';
        case 'Telematica_Sincrona':
            return 'sincrona';
        case 'Presenza':
            return 'presenza';
        case 'Telematica_Mista':
          return 'telematica_mista';    
        case 'Mista_Presenza/Telematica':
          return 'mista_presenza/telematica';     
        default:
            return 'Nessun opzione'; // 'Nessuna opzione' con la 'a' se preferito
    }
  };

   static rolesAllowedToCreate = (user) => {
    switch (user?.role) {
        case ROLES.ADMIN:
        case ROLES.DEVELOPER:
            return ["superpartner", "admin", "course_designer", "docente", "partner", "tutor", "corsista"];
        
        // --- BLOCCO DA AGGIUNGERE ---
        case ROLES.SUPERPARTNER:
            return ["partner", "corsista"];
        // -----------------------------

        case ROLES.COURSE_DESIGNER:
            return ["partner"];
        case ROLES.PARTNER:
            return ["corsista"];
        default:
          return [];
    }
  };

  static getPaymentState = (state) => {
    switch (state) {
        case "Non_pagato":
            return "Non pagato";
        case "Pagato":
            return "Pagato";
        case "Parzialmente_pagato":
            return "Parzialmente pagato";
        default:
            return ""; // O un valore di default più esplicito come "Sconosciuto"
    }
  };
  
  static getBadgeColorClass = (paymentState) => { // Per stato pagamento
    switch(paymentState) {
      case 'Pagato':
        return 'text-bg-success';
      case 'Parzialmente_pagato':
        return 'text-bg-warning';
      case 'Non_pagato':
      default: // Include 'Non_pagato' e qualsiasi altro stato non mappato
        return 'text-bg-danger';
    }
  };

  // Metodi per lo STATO FATTURAZIONE
  static getBillingStatusText(status, numeroFattura) {
    switch (status?.trim()) {
      case 'Fatturato':
        // Se lo stato è "Fatturato" E abbiamo un numero di fattura, mostriamolo.
        if (numeroFattura) {
          return `Fattura n. ${numeroFattura}`;
        }
        // Altrimenti, mostriamo solo "Fatturato".
        return 'Fatturato';

      case 'Non_fatturato':
        return 'Da fatturare';

      default:
        return 'Sconosciuto';
    }
  }
  static getBillingStatusBadgeColor(status) {
    switch (status) {
      case 'Non_fatturato': // Corrisponde a "Da fatturare" nell'UI
        return 'bg-warning text-dark';
      case 'Fatturato':
        return 'bg-success text-light';
      default:
        return 'bg-secondary text-light';
    }
  }
  // Fine metodi per stato fatturazione

  static getVerificaStatusBadge(status) {
        switch (status) {
            case 'conforme':
                return { bg: 'success', text: 'Conforme' };
            case 'non_conforme':
                return { bg: 'danger', text: 'Non Conforme' };
            case 'da_verificare':
            default:
                return { bg: 'warning', text: 'Da Verificare' };
        }
    }

  /**
   * Dati da utenti.email_consegna_* (job bounce IMAP). Null se nessun problema noto.
   * @param {object} utente
   * @returns {{ variant: string, title: string, text: string } | null}
   */
  static getEmailConsegnaAlert(utente) {
        const stato = utente?.email_consegna_stato;
        if (!stato || stato === 'ok') {
            return null;
        }
        const when = utente?.email_consegna_aggiornato_at
            ? UtilityService.formatBounceRilevamentoAt(utente.email_consegna_aggiornato_at)
            : null;
        const snippet = (utente?.email_consegna_messaggio || '').trim();
        const title = 'Problema di recapito email';
        let text = '';
        if (stato === 'hard_bounce') {
            text = 'Il server della casella ha rifiutato la consegna (indirizzo inesistente o permanentemente non valido). Verificare l\'email con il partner o correggerla in anagrafica.';
        } else if (stato === 'soft_bounce') {
            text = 'Casella piena o errore temporaneo del server di posta. L\'utente potrebbe non aver ricevuto le comunicazioni.';
        } else {
            text = 'È stata registrata una notifica di errore di recapito. Verificare l\'indirizzo e la casella.';
        }
        if (snippet) {
            text += ' Dettaglio: ' + snippet;
        }
        if (when) {
            text += ' (rilevamento: ' + when + ')';
        }
        const variant = stato === 'soft_bounce' ? 'warning' : 'danger';
        return { variant, title, text };
    }

  static validationForm = (formData, requiredFields) => {
    if (!formData || !requiredFields) return []; // Aggiunto controllo per argomenti nulli
    return requiredFields.filter(field => {
      const value = formData[field]?.toString();
      return !value || value.trim() === '';
    });
  };

  static transformPropertyName = (object, oldProperty, newProperty) => {
    if (!object) return {}; // Gestisci oggetto nullo
    const transformedObject = { ...object };

    if (Object.prototype.hasOwnProperty.call(transformedObject, oldProperty)) { // Modo più sicuro per hasOwnProperty
        transformedObject[newProperty] = transformedObject[oldProperty];
        delete transformedObject[oldProperty];
    }
    return transformedObject;
  };

  static generatePassword = () => {
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const specialChars = '!@#$%^&*()-_=+[{]};:<>|./?';
    const numbers = '0123456789';

    const getRandomChar = (charSet) => {
        if (!charSet || charSet.length === 0) return ''; // Gestisci charSet vuoto
        const randomIndex = Math.floor(Math.random() * charSet.length);
        return charSet[randomIndex];
    };

    let password = '';
    password += getRandomChar(uppercaseChars);
    password += getRandomChar(lowercaseChars);
    password += getRandomChar(specialChars);
    password += getRandomChar(numbers);

    const allChars = uppercaseChars + lowercaseChars + specialChars + numbers;
    const remainingLength = Math.max(0, 8 - password.length); // Assicura che non sia negativo

    for (let i = 0; i < remainingLength; i++) {
        password += getRandomChar(allChars);
    }
    // Mescola la password per una maggiore casualità
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    return password;
  };

  static formatDateFillInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  static generateCourseCode() {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const charactersLength = characters.length;
    let code = '';
    for (let i = 0; i < 16; i++) {
      code += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return code;
  }

  static generateIconContenuti(contenuto) {
    // Se questo file è .js e non .jsx, e usi un setup che non traspila JSX in file .js,
    // potresti dover restituire stringhe di classi o usare React.createElement.
    // Se usi <span className="...">, assicurati che React sia importato in cima al file.
    switch (contenuto?.tipo_contenuto) {
      case 'testo-immagini':
        return <span className="bi bi-file-earmark-font text-dark"></span>;
      case 'audio-video':
        return <span className="bi bi-file-earmark-play text-dark"></span>;
      case 'pdf':
        return <span className="bi bi-file-earmark-pdf"></span>;
      case 'test':
        return <span className="bi bi-file-earmark-text"></span>;
      default:
        return <span className="bi bi-file-earmark-font text-dark"></span>; 
    }
  }

} // Parentesi graffa finale che chiude la classe UtilityService